import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  useTheme,
  Alert,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

interface RateLimitData {
  totalAccounts: number;
  riskAccounts: number;
  status: 'HEALTHY' | 'WARNING' | 'CRITICAL';
  message: string;
}

const RateLimitMonitor = () => {
  const theme = useTheme();
  const [rateLimitData, _setRateLimitData] = useState<RateLimitData>({
    totalAccounts: 5,
    riskAccounts: 0,
    status: 'HEALTHY',
    message: 'All accounts comfortably below throttling limits',
  });
  const [loading, setLoading] = useState(false);

  const handleRefresh = async () => {
    setLoading(true);
    // TODO: Fetch from API
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'HEALTHY':
        return theme.palette.success.main;
      case 'WARNING':
        return theme.palette.warning.main;
      case 'CRITICAL':
        return theme.palette.error.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  return (
    <Card
      sx={{
        borderRadius: '10px',
        boxShadow: '0 10px 30px rgba(146,153,184,0.15)',
        border: 'none',
      }}
    >
      <CardContent sx={{ padding: '25px !important' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            Rate Limit Monitor
          </Typography>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {/* Status Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                textAlign: 'center',
                p: 2,
                bgcolor: theme.palette.background.default,
                borderRadius: '8px',
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {rateLimitData.totalAccounts}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Accounts
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                textAlign: 'center',
                p: 2,
                bgcolor: theme.palette.background.default,
                borderRadius: '8px',
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {rateLimitData.riskAccounts}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Risk Accounts
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                textAlign: 'center',
                p: 2,
                bgcolor: theme.palette.background.default,
                borderRadius: '8px',
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: getStatusColor(rateLimitData.status),
                }}
              >
                {rateLimitData.status}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Status
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Status Message */}
        {rateLimitData.status === 'HEALTHY' && (
          <Alert
            icon={<CheckCircleIcon />}
            severity="success"
            sx={{ mb: 3 }}
          >
            {rateLimitData.message}
          </Alert>
        )}

        {/* Rate Limit Formulas */}
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, mb: 2 }}
        >
          Facebook Rate Limit Formulas - Standard Access
        </Typography>

        <Grid container spacing={2}>
          {/* Ads Management API */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '8px',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Ads Management API
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                100,000 + 40 * Active Ads per hour
              </Typography>

              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6">100,000</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Base Limit
                    </Typography>
                    <Typography variant="caption" display="block">
                      calls/hour
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6">+40</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Per Active Ad
                    </Typography>
                    <Typography variant="caption" display="block">
                      calls/hour
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6">120,000</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Example (500 ads)
                    </Typography>
                    <Typography variant="caption" display="block">
                      calls/hour
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Ads Insights API */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '8px',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Ads Insights API
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                190,000 + 400 * Active Ads - 0.001 * User Errors per hour
              </Typography>

              <Grid container spacing={1}>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6">190,000</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Base Limit
                    </Typography>
                    <Typography variant="caption" display="block">
                      calls/hour
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6">+400</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Per Active Ad
                    </Typography>
                    <Typography variant="caption" display="block">
                      calls/hour
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6">-0.001</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Error Penalty
                    </Typography>
                    <Typography variant="caption" display="block">
                      per error
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6">390,000</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Example (500 ads)
                    </Typography>
                    <Typography variant="caption" display="block">
                      calls/hour
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>

        {/* No Data Message */}
        <Alert severity="info" sx={{ mt: 3 }}>
          No rate limit data available from Facebook API
          <Typography variant="caption" display="block">
            Data is only available after making API requests
          </Typography>
        </Alert>
      </CardContent>
    </Card>
  );
};

export default RateLimitMonitor;
